/* eslint-disable */
import { useCallback, useState } from 'react';
import Prompt from './Prompt';
import context from './context';
import Portal from '../Portal';

const PromptProvider = ({children}) => {
  const [prompts, setPrompts] = useState([]);

  /**
   * Cancels and closes a prompt
   *
   * @type {(id: string) => void}
   */
  const cancelPrompt = useCallback((id) => {
    setPrompts(prompts => {
      let found = false;
      const newPrompts = prompts.filter(prompt => {
        found = found || prompt.id === id;
        prompt.reject();
        return prompt.id !== id;
      });

      return found ? newPrompts : prompts;
    });
  }, []);

  /**
   * Creates a new prompt and returns a promise that will me resolved when the
   * user interacts with the prompt or reject if the prompt is cancelled programmatically
   * or dismissed
   *
   * @template T
   * @type {(args: {
   *   icon?: JSX.Element;
   *   content: string;
   *   options: Array<{
   *     id: string|number;
   *     content: string;
   *     secondary?: boolean;
   *   }>
   * }) => [Promise<T>, () => void]}
   */
  const prompt = useCallback(({icon, content, options}) => {
    let resolve, reject;
    const id = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const cancel = () => cancelPrompt(id);
    const promise = new Promise((rs, rj) => {
      resolve = rs;
      reject = rj;
    });

    setPrompts(ps => [
      ...ps,
      {
        id,
        resolve,
        reject,
        content,
        icon,
        options,
      },
    ]);

    return [promise, cancel];
  }, []);

  /**
   * Handles the selection of a option for a prompt
   *
   * @type {(
   *   promptId: string,
   *   optionId: string|number,
   * ) => void}
   */
  const handleSelect = useCallback((promptId, optionId) => {
    setPrompts(ps => ps.filter(prompt => {
      if (prompt.id !== promptId) return true;
      prompt.resolve(optionId);
      return false;
    }));
  }, []);

  return (
    <context.Provider value={{prompts, setPrompts, prompt, cancelPrompt}}>
      {children}
      <Portal element="#modal-root">
        {prompts.map(prompt =>
          <Prompt
            key={prompt.id}
            onDismiss={() => cancelPrompt(prompt.id)}
            onSelect={(optionId) => handleSelect(prompt.id, optionId)}
            options={prompt.options}
            icon={prompt.icon}
            children={prompt.content}
          />
        )}
      </Portal>
    </context.Provider>
  );
};

export default PromptProvider;
